import React from "react";
import { exportFile } from "../../../actions/DataExporter";
import RunHistoryDetails from "./RunHistoryDetails";
import Window from "../../2/Controls/Window";
import ExportButton from "../../2/Controls/ExportButton";
import CampaignHeaderSection from "./CampaignHeaderSection";
import CampaignStats from "./CampaignStats";
import PageSection from "../../2/Controls/PageSection";
import Page from "../../2/Controls/Page";
import { getProperty } from "../../../CommonFunctions";

// Import from react-icons instead of lucide-react
import { 
  FiArrowLeft, 
  FiEdit, 
  FiPlay, 
  FiPause, 
  FiClock, 
  FiUsers, 
  FiMail, 
  FiCopy, 
  FiCheckCircle, 
  FiActivity, 
  FiCalendar 
} from 'react-icons/fi';
import { BiBarChartAlt } from 'react-icons/bi';
import { MdMessage, MdChat, MdMessageCircle } from 'react-icons/md';
import { AiOutlineMessage, AiOutlineSparkles } from 'react-icons/ai';
import { FaRegCommentDots } from 'react-icons/fa';
import NewButton from "../../2/Controls/NewButton";

function formatDateTime(isoString) {
  if (!isoString) return "Never Run";
  const date = new Date(isoString);
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
}

class CampaignManage extends React.Component {
  constructor(props) {
    super(props);

    this.id = this.props.match.params.id;
    
    this.state = {
      campaign: this.props.location && this.props.location.state ? this.props.location.state.campaignData : undefined,
      loading: false,
      showRunHistoryDetails: false,
      currentRunHistoryDate: null,
    };
  }

  handleStatusChange = async (newStatus) => {
    this.setState({ loading: true });
    try {
      const response = await this.props.updateCampaignStatus(
        getProperty(this.state.campaign, "_id"),
        newStatus
      );
      if (response.code === "0") {
        this.setState({ campaign: { ...this.state.campaign, Status: newStatus } });
        this.props.getToastr(`Campaign ${newStatus.toLowerCase()} successfully!`, {
          icon: "✓",
          style: {
            borderRadius: "10px",
            background: "#10B981",
            color: "#fff",
          },
        });
      } else {
        this.props.getToastr("Unable to update campaign status");
      }
    } catch (err) {
      this.props.getToastr("Error updating campaign status");
      console.error(err);
    } finally {
      this.setState({ loading: false });
    }
  };

  getStatusBadge = (status) => {
    const configs = {
      Active: {
        bg: "#10B981",
        icon: <FiCheckCircle size={14} />,
        text: "#FFFFFF",
      },
      Paused: {
        bg: "#F59E0B",
        icon: <FiPause size={14} />,
        text: "#FFFFFF",
      },
      Draft: {
        bg: "#94A3B8",
        icon: <FiClock size={14} />,
        text: "#FFFFFF",
      },
    };
    const config = configs[status] || configs.Draft;

    return (
      <div
        style={{
          backgroundColor: config.bg,
          color: config.text,
          padding: "6px 16px",
          borderRadius: "9999px",
          fontSize: "14px",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {config.icon}
        <span>{status}</span>
      </div>
    );
  };

  getStatusButton = () => {
    const buttonStyle = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "0 16px",  // Changed from "8px 16px" to vertical padding 0
    height: "34px",      // Added fixed height
      borderRadius: "8px",
      fontWeight: 500,
      transition: "all 0.2s ease",
      fontSize: "14px",
      border: "none",
      cursor: "pointer",
      opacity: this.state.loading ? 0.5 : 1,
      pointerEvents: this.state.loading ? "none" : "auto",
    };

    const campaignStatus = getProperty(this.state.campaign, "Status");

    if (campaignStatus === "Draft") {
      return (
        <button
          onClick={() => this.handleStatusChange("Active")}
          disabled={this.state.loading}
          style={{
            ...buttonStyle,
            backgroundColor: "#2563EB",
            color: "#FFFFFF",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#1D4ED8";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#2563EB";
            e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
          }}
        >
          <FiPlay size={16} />
          <span style={{ display: "inline" }}>Activate Campaign</span>
        </button>
      );
    }
    if (campaignStatus === "Active") {
      return (
        <button
          onClick={() => this.handleStatusChange("Paused")}
          disabled={this.state.loading}
          style={{
            ...buttonStyle,
            backgroundColor: "#D97706",
            color: "#FFFFFF",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#B45309";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#D97706";
            e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
          }}
        >
          <FiPause size={16} />
          <span style={{ display: "inline" }}>Pause Campaign</span>
        </button>
      );
    }
    if (campaignStatus === "Paused") {
      return (
        <button
          onClick={() => this.handleStatusChange("Active")}
          disabled={this.state.loading}
          style={{
            ...buttonStyle,
            backgroundColor: "#059669",
            color: "#FFFFFF",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#047857";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#059669";
            e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
          }}
        >
          <FiPlay size={16} />
          <span style={{ display: "inline" }}>Resume Campaign</span>
        </button>
      );
    }
  };

  handleCopy = () => {
    const idToCopy = getProperty(this.state.campaign, "_id");
    if (idToCopy) {
      navigator.clipboard.writeText(idToCopy.toUpperCase());
      this.props.getToastr("Campaign ID copied to clipboard!");
    } else {
      this.props.getToastr("Campaign ID not available.");
    }
  };

  handleExportExecutionHistory = (format) => {
    const history = getProperty(this.state.campaign, "RunHistory") || [];

    if (!history.length) {
      this.props.getToastr("No execution history available");
      return;
    }

    const columns = [
      {
        header: "Run Number",
        field: "runNumber",
        type: "number"
      },
      {
        header: "Executed On",
        field: "executedOn",
        type: "date"
      },
      {
        header: "Total Customers",
        field: "totalCustomers",
        type: "number"
      },
      {
        header: "WhatsApp Sent",
        field: "whatsappSent",
        type: "number"
      },
      {
        header: "SMS Sent",
        field: "smsSent",
        type: "number"
      },
      {
        header: "Email Sent",
        field: "emailSent",
        type: "number"
      }
    ];

    const exportData = history.map((run, index) => ({
      runNumber: index + 1,
      executedOn: getProperty(run, "RunAt"),
      totalCustomers: getProperty(run, "TotalCustomers") || 0,
      whatsappSent: getProperty(run, "WhatsappCount") || 0,
      smsSent: getProperty(run, "SMSCount") || 0,
      emailSent: getProperty(run, "EmailCount") || 0
    }));

    exportFile({
      data: exportData,
      columns: columns,
      fileType: format,
      title: `${getProperty(this.state.campaign, "Name") || "campaign"}_execution_history`,
      landscape: false,
      allowEmpty: false
    });

    this.props.getToastr(`${format} exported successfully!`);
  };

  handleCancel = () => {
    this.props.history.push({
      pathname:"/home/campaign"
    });
  };

  handlePageButtonClick = (buttonType) => {
    if (buttonType === "cancel") {
      this.handleCancel();
    } else if (buttonType === "save") {
      console.log("Save clicked");
    }
  };

  render() {
    const { campaign, loading, showRunHistoryDetails, currentRunHistoryDate } = this.state;

    const runHistory = getProperty(campaign, "RunHistory") || [];
    
    const totalStats = runHistory.reduce(
      (acc, run) => ({
        customers: acc.customers + (getProperty(run, "TotalCustomers") || 0),
        whatsapp: acc.whatsapp + (getProperty(run, "WhatsappCount") || 0),
        sms: acc.sms + (getProperty(run, "SMSCount") || 0),
        email: acc.email + (getProperty(run, "EmailCount") || 0),
      }),
      { customers: 0, whatsapp: 0, sms: 0, email: 0 }
    );

    if (!campaign) {
      return (
        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "#F9FAFB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                animation: "spin 1s linear infinite",
                borderRadius: "50%",
                width: "48px",
                height: "48px",
                border: "3px solid #E5E7EB",
                borderTopColor: "#2563EB",
                margin: "0 auto 16px",
              }}
            ></div>
            <p style={{ color: "#4B5563", fontWeight: 500 }}>
              Loading campaign...
            </p>
          </div>
        </div>
      );
    }

    const footerButtons = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "8px",
        }}
      >
        {this.getStatusButton()}
        <button
          onClick={() => {
            this.props.history.push({
              pathname:"/home/campaign/create",
              state: { campaignData: campaign }
            });
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 20px",
            backgroundColor: "#111827",
            color: "#FFFFFF",
            borderRadius: "8px",
            fontWeight: 500,
            transition: "all 0.2s ease",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#1F2937";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#111827";
          }}
        >
          <span>Edit</span>
        </button>
      </div>
    );

    return (
      <Page
        title={getProperty(campaign, "Name")}
        showCancelButton={false}
        showSaveButton={false}
        onClick={this.handlePageButtonClick}
        processing={loading}
      >
        <div style={{ display: "grid"}}>
          <CampaignHeaderSection
            campaign={campaign}
            handleCopy={this.handleCopy}
            totalStats={totalStats}
            formatDateTime={formatDateTime}
            getStatusBadge={this.getStatusBadge}
          />

        <PageSection
  title={
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
          fontSize: "14px",
      }}
    >
      {/* Left side - Title and count */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div>
          <h2
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
              margin: 0,
            }}
          >
            Execution History
          </h2>
          <p
            style={{
              fontSize: "12px",
              color: "#4B5563",
              margin: "4px 0 0 0",
            }}
          >
            {runHistory.length} execution(s)
          </p>
        </div>
      </div>

      {/* Right side - Action buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {/* Edit button */}
          <div  style={{width:"200px" }}> <NewButton
                      type="secondary"
                      text="Edit"
                     onClick={() => {
            this.props.history.push({
              pathname: "/home/campaign/create",
              state: { campaignData: campaign }
            });
          }}
                    />
     
     </div>

        {/* Status button (Activate/Pause/Resume) */}
        {this.getStatusButton()}

        {/* Export button */}
        <ExportButton
          onExport={this.handleExportExecutionHistory}
          loading={false}
        />
      </div>
    </div>
  }
>
  {/* Execution history content remains the same */}
  <div>
    {runHistory.length > 0 ? (
      <div style={{ display: "grid", gap: "28px" }}>
                  {[...runHistory].reverse().map((run, index) => (
                    <div
                      key={getProperty(run, "_id") || index}
                      style={{
                        border: "1px solid #D1D5DB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderRadius: "16px",
                        padding: "12px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            gap: "16px",
                            marginBottom: "8px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                backgroundColor: "#DBEAFE",
                                color: "#111827",
                                borderRadius: "8px",
                                fontWeight: 600,
                                fontSize: "14px",
                                flexShrink: 0,
                              }}
                            >
                              #{runHistory.length - index}
                            </div>
                            <div>
                              <p
                                style={{
                                  fontSize: "12px",
                                  color: "#6B7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  marginBottom: "4px",
                                }}
                              >
                                Executed On
                              </p>
                              <p
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  color: "#111827",
                                }}
                              >
                                {formatDateTime(getProperty(run, "RunAt"))}
                              </p>
                            </div>
                          </div>
                        </div>
                        <CampaignStats
                          run={run}
                          campaign={campaign}
                          key={index}
                        />
                      </div>
                      <button
                        onClick={() => {
                          this.setState({ 
                            showRunHistoryDetails: true,
                            currentRunHistoryDate: getProperty(run, "RunAt")
                          });
                        }}
                        style={{
                          padding: "4px 12px",
                          backgroundColor: "#2563EB",
                          color: "#FFFFFF",
                          fontSize: "16px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          border: "none",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#1E40AF";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#2563EB";
                        }}
                      >
                        See Details
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "64px 0",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "64px",
                      height: "64px",
                      backgroundColor: "#F3F4F6",
                      borderRadius: "9999px",
                      marginBottom: "16px",
                    }}
                  >
                    <FiClock color="#9CA3AF" size={32} />
                  </div>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#111827",
                      marginBottom: "8px",
                    }}
                  >
                    No Execution History
                  </h3>
                  <p
                    style={{
                      color: "#4B5563",
                      marginBottom: "24px",
                      fontSize: "16px",
                    }}
                  >
                    This campaign hasn&apos;t been executed yet
                  </p>
                  {getProperty(campaign, "Status") === "Draft" && (
                    <button
                      onClick={() => this.handleStatusChange("Active")}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "12px 24px",
                        backgroundColor: "#2563EB",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1D4ED8";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#2563EB";
                      }}
                    >
                      <FiPlay size={18} />
                      Activate Campaign to Start
                    </button>
                  )}
                </div>
              )}
            </div>
          </PageSection>
        </div>

        

        {showRunHistoryDetails && (
          
            <RunHistoryDetails
              {...this.props}
              showRunHistoryDetails={showRunHistoryDetails}
              setShowRunHistoryDetails={(value) => this.setState({ showRunHistoryDetails: value })}
              campaign={campaign}
              currentRunHistoryDate={currentRunHistoryDate}
            />
        )}
      </Page>
    );
  }
}

export default CampaignManage;